function escapeHtml (value) {
  if (value === null || value === undefined) return ''
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function escapeRegExp (value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function highlightHtml (raw, term) {
  const text = String(raw ?? '')
  if (!term) return escapeHtml(text)
  const pattern = new RegExp(escapeRegExp(term), 'gi')
  let out = ''
  let last = 0
  for (const match of text.matchAll(pattern)) {
    out += escapeHtml(text.slice(last, match.index))
    out += `<strong class="font-semibold text-black">${escapeHtml(match[0])}</strong>`
    last = match.index + match[0].length
  }
  out += escapeHtml(text.slice(last))
  return out
}

const STAR_FULL = '<svg width="100%" height="100%" viewBox="0 0 24 24" fill="currentColor" class="w-full h-full" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.007 5.404.433c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.433 2.082-5.006z"/></svg>'

function starRowHtml (rating, size = 'w-4.5 h-4.5') {
  const full = Math.max(0, Math.min(5, Math.floor(rating)))
  const empty = 5 - full
  let html = ''
  for (let i = 0; i < full; i += 1) {
    html += `<span class="tb-icon inline-block ${size}">${STAR_FULL}</span>`
  }
  for (let i = 0; i < empty; i += 1) {
    html += `<span class="tb-icon inline-block ${size} text-clay">${STAR_FULL}</span>`
  }
  return html
}

const CHECK_ICON = '<svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" class="w-full h-full" xmlns="http://www.w3.org/2000/svg"><polyline points="20 6 9 17 4 12"/></svg>'

const HEART_ICON = '<svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" class="w-full h-full" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12Z"/></svg>'

const HEART_ICON_FILLED = '<svg width="100%" height="100%" viewBox="0 0 24 24" fill="currentColor" class="w-full h-full" xmlns="http://www.w3.org/2000/svg"><path d="M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 01-.383-.218 25.18 25.18 0 01-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0112 5.052 5.5 5.5 0 0116.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 01-4.244 3.17 15.247 15.247 0 01-.383.219l-.022.012-.007.004-.003.001a.752.752 0 01-.704 0l-.003-.001z"/></svg>'

const PHOTO_SPINNER_HTML = '<span class="product-reviews__card-image__spinner absolute inset-0 hidden items-center justify-center bg-white/80" aria-hidden="true"><span class="inline-block w-5 h-5 border-2 border-iron/30 border-t-black rounded-full animate-spin"></span></span>'

function formatDate (iso) {
  if (!iso) return ''
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return ''
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
  } catch {
    return ''
  }
}

const I18N_FALLBACKS = {
  verifiedBuyer: 'Verified buyer',
  giftedProduct: 'Gifted product',
  sourceBadge: 'Review from [[source]]',
  defaultAuthor: 'Verified buyer',
  defaultReplyAuthor: 'TentBox',
  openPhotoAria: 'Open review photo',
  starsAria: '[[rating]] out of 5 stars',
  reviewFor: 'Review for',
  readMore: 'Read more',
  readLess: 'Read less',
  helpfulPrompt: 'Helpful?',
  helpfulCount: '[[count]] people found this helpful',
  helpfulCountSingular: '[[count]] person found this helpful',
  helpfulCountMobile: '[[count]] found this helpful',
  helpfulVoteAria: 'Mark this review as helpful',
  helpfulVotedAria: 'You found this review helpful',
  originWebsite: '[[author]] – Reviewed on our [[site]] website',
  originCountry: '[[author]] in [[country]]',
  originSites: { uk: 'UK', 'eu-en': 'European', fr: 'French', de: 'German' },
  loadingList: 'Loading reviews…',
  loadingButton: 'Loading…',
  errorLabel: 'Could not load more reviews.',
  errorRetry: 'Try again',
  loadMoreLabel: 'Load more reviews',
  loadMoreCount: '([[count]] more)',
  resultsSummary: 'Showing [[count]] results for "[[term]]"',
  resultsSummarySingular: 'Showing [[count]] result for "[[term]]"',
  clearSearch: 'Clear search',
  clearAll: 'Clear all',
  removeFilterAria: 'Remove filter',
  sheetShow: 'Show [[count]] reviews',
  sheetShowSingular: 'Show [[count]] review',
  sortLabels: { recent: 'Most recent', helpful: 'Most helpful', 'rating-desc': 'Highest rated', 'rating-asc': 'Lowest rated' },
  photosOnlyLabel: 'Photos & videos only',
}

function interpolate (template, values) {
  if (!template) return ''
  return String(template).replace(/\[\[(\w+)\]\]/g, (_, key) => {
    const value = values ? values[key] : undefined
    if (value === undefined || value === null) return ''
    return String(value)
  })
}

function reviewRating (review) {
  const value = Number(review?.rating)
  return Number.isFinite(value) ? value : 0
}

function reviewOrderKey (review) {
  if (review?.id !== null && review?.id !== undefined && review.id !== '') return String(review.id)
  return `${review?.author || ''}|${review?.created || ''}|${String(review?.content || '').slice(0, 40)}`
}

function countryName (code, locale) {
  if (!code) return ''
  try {
    const names = new Intl.DisplayNames([locale || 'en'], { type: 'region' })
    return names.of(String(code).toUpperCase()) || ''
  } catch {
    return ''
  }
}

const HOME_COUNTRIES = { uk: 'GB', 'eu-en': null, fr: 'FR', de: 'DE' }

const DEFAULT_STATE = { search: '', component: 'all', photosOnly: false, sort: 'recent' }

const AI_SUMMARY_CLAMP = 'line-clamp-4'
const SUMMARY_COLLAPSED = 'product-reviews__summary--collapsed'

class TbProductReviews extends HTMLElement {
  constructor () {
    super()
    this.handleHostClick = this.handleHostClick.bind(this)
    this.handleSectionLoad = this.handleSectionLoad.bind(this)
    this.handleDocKeydown = this.handleDocKeydown.bind(this)
  }

  connectedCallback () {
    this.addEventListener('click', this.handleHostClick)
    document.addEventListener('shopify:section:load', this.handleSectionLoad)
    document.addEventListener('keydown', this.handleDocKeydown)

    if (this._reviewsReady) {
      this.refreshDomRefs()
      return
    }

    const jsonEl = this.querySelector('script[type="application/json"][data-reviews-json]')
    if (!jsonEl) return

    this._reviewsReady = true

    try {
      let parsed = JSON.parse(jsonEl.textContent || '{}')
      if (typeof parsed === 'string') parsed = JSON.parse(parsed)
      this.seedReviews = Array.isArray(parsed?.reviews) ? parsed.reviews : Array.isArray(parsed) ? parsed : []
    } catch (err) {
      console.warn('[tb-product-reviews] Failed to parse reviews JSON:', err)
      this.seedReviews = []
    }

    const configEl = this.querySelector('script[type="application/json"][data-reviews-config]')
    let config = {}
    if (configEl) {
      try {
        config = JSON.parse(configEl.textContent || '{}')
      } catch (err) {
        console.warn('[tb-product-reviews] Failed to parse config JSON:', err)
      }
    }
    this.config = config
    this.i18n = { ...I18N_FALLBACKS, ...(config.i18n || {}) }
    // Product tags on cards only make sense when reviews are pooled from
    // several products (bundle components). Single-product pages skip them.
    this.hasComponents = Array.isArray(config.byComponent) && config.byComponent.some(comp => comp && comp.title)
    this.flags = config.flags || {}
    this.componentProducts = config.componentProducts || {}
    this.homeOrigin = config.homeOrigin || this.dataset.homeOrigin || 'uk'
    this.productTitle = config.productTitle || this.dataset.productTitle || ''

    this.locale = (document.documentElement.lang || this.dataset.locale || 'en').toLowerCase()
    this.initialCount = Math.max(1, parseInt(this.dataset.initialCount || '6', 10))
    this.pageSize = Math.max(1, parseInt(this.dataset.pageSize || '6', 10))
    this.total = parseInt(this.dataset.total || '0', 10) || 0
    this.hasMoreOnServer = this.dataset.hasMore === 'true'
    this.appUrl = (this.dataset.appUrl || '').replace(/\/$/, '')
    this.store = this.dataset.store || ''
    this.productId = this.dataset.productId || ''
    this.canUseApi = Boolean(this.appUrl && this.store && this.productId)
    this.canPaginateApi = Boolean(this.hasMoreOnServer && this.canUseApi)

    this.state = {
      ...DEFAULT_STATE,
      visible: this.initialCount,
      apiCursor: null,
      apiExhausted: false,
      loading: false,
      serverRows: [],
      serverCursor: null,
      serverExhausted: false,
      serverTotal: null,
    }
    this.draft = { ...DEFAULT_STATE }

    this.apiReviews = []
    this.knownIds = new Set(this.seedReviews.map(r => r.id).filter(Boolean))
    this._fetchSeq = 0
    this._countCache = new Map()

    this.votedCounts = this.loadVotedCounts()
    this.votedIds = new Set(Object.keys(this.votedCounts))

    this.refreshDomRefs()

    const ssrCardCount = this.listEl ? this.listEl.querySelectorAll('[data-review-id]').length : 0
    if (this.seedReviews.length === 0 && ssrCardCount > 0) {
      this.hydrateSeedFromDom()
    }

    this.bindControlEvents()
    if (this.seedReviews.length > 0) {
      this.render()
    }
  }

  disconnectedCallback () {
    this._reviewsReady = false
    if (this._pillsResizeObserver) {
      this._pillsResizeObserver.disconnect()
      this._pillsResizeObserver = null
    }
    if (this._pillsScrollEl && this.updatePillsFade) {
      this._pillsScrollEl.removeEventListener('scroll', this.updatePillsFade)
      this._pillsScrollEl = null
    }
    if (this._aiSummaryObserver) {
      this._aiSummaryObserver.disconnect()
      this._aiSummaryObserver = null
      this._aiSummaryObserved = null
    }
    document.removeEventListener('shopify:section:load', this.handleSectionLoad)
    document.removeEventListener('keydown', this.handleDocKeydown)
    if (this._sheetPortaled && !this.isConnected) {
      this._sheetPortaled.remove()
      this._sheetPortaled = null
    }
  }

  bindSheetEvents (sheet) {
    if (sheet._tbEventsBound) return
    sheet._tbEventsBound = true

    sheet.addEventListener('click', (event) => {
      if (event.target.closest('[data-sheet-close]')) {
        this.closeSheet()
        return
      }

      const sortPill = event.target.closest('[data-sheet-sort-pill]')
      if (sortPill) {
        this.draft.sort = sortPill.dataset.sheetSortPill || 'recent'
        this.syncSheetUi()
        this.scheduleSheetCount()
        return
      }

      const componentPill = event.target.closest('[data-sheet-component-pill]')
      if (componentPill) {
        this.draft.component = componentPill.dataset.sheetComponentPill || 'all'
        this.syncSheetUi()
        this.scheduleSheetCount()
        return
      }

      if (event.target.closest('[data-sheet-reset]')) {
        this.draft = { ...DEFAULT_STATE }
        this.syncSheetUi()
        this.scheduleSheetCount()
        return
      }

      if (event.target.closest('[data-sheet-apply]')) {
        this.closeSheet()
        this.applyState({ ...this.draft })
      }
    })

    sheet.addEventListener('input', (event) => {
      const search = event.target.closest('[data-sheet-search]')
      if (search) {
        this.draft.search = search.value.trim()
        this.scheduleSheetCount()
      }
    })

    sheet.addEventListener('change', (event) => {
      const photos = event.target.closest('[data-sheet-photos-only]')
      if (photos) {
        this.draft.photosOnly = photos.checked
        this.scheduleSheetCount()
      }
    })

    document.querySelector('#tb_page_obscure')?.addEventListener('click', () => {
      if (!sheet.classList.contains('hidden')) this.closeSheet()
    })

    window.addEventListener('hide:page-obscure', () => {
      if (!sheet.classList.contains('hidden')) this.closeSheet()
    })
  }

  handleDocKeydown (event) {
    if (!this.sheetEl || this.sheetEl.classList.contains('hidden')) return
    if (event.key === 'Escape') {
      this.closeSheet()
      return
    }
    if (event.key === 'Tab') {
      this.trapFocus(event, this.sheetEl.querySelector('.product-reviews__sheet-panel'))
    }
  }

  trapFocus (event, container) {
    if (!container) return
    const focusables = container.querySelectorAll('button:not([disabled]), input, select, a[href], [tabindex]:not([tabindex="-1"])')
    if (!focusables.length) return
    const first = focusables[0]
    const last = focusables[focusables.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    } else if (!container.contains(document.activeElement)) {
      event.preventDefault()
      first.focus()
    }
  }

  hydrateSeedFromDom () {
    if (!this.listEl) return
    const cards = this.listEl.querySelectorAll('[data-review-id]')
    if (!cards.length) return
    this.seedReviews = Array.from(cards).map(card => {
      const timeEl = card.querySelector('time')
      const helpfulBtn = card.querySelector('[data-helpful]')
      return {
        id: card.dataset.reviewId,
        rating: Number(card.dataset.reviewRating) || 0,
        verified: card.dataset.reviewVerified === 'true',
        gifted: card.dataset.reviewGifted === 'true',
        created: timeEl?.getAttribute('datetime') || '',
        title: card.querySelector('.product-reviews__card-title')?.textContent?.trim() || '',
        content: card.querySelector('.product-reviews__card-content')?.textContent?.trim() || '',
        author: card.querySelector('.product-reviews__card-author')?.textContent?.trim() || '',
        helpful_count: Number(helpfulBtn?.dataset.helpfulCount) || 0, // eslint-disable-line camelcase
      }
    })
    this.knownIds = new Set(this.seedReviews.map(review => review.id).filter(Boolean))
  }

  refreshDomRefs () {
    this.listEl = this.querySelector('[data-review-list]')
    this.emptyEl = this.querySelector('[data-empty]')
    this.loadMoreBtn = this.querySelector('[data-load-more]')
    this.loadMoreLabelEl = this.querySelector('[data-load-more-label]')
    this.loadMoreCountEl = this.querySelector('[data-load-more-count]')
    this.loadMoreDefaultLabel = this.loadMoreLabelEl
      ? this.loadMoreLabelEl.textContent.trim() || this.i18n.loadMoreLabel
      : this.i18n.loadMoreLabel
    this.searchInput = this.querySelector('[data-search-input]')
    this.photosOnlyInput = this.querySelector('[data-photos-only]')
    this.sortSelect = this.querySelector('[data-sort-select]')
    this.resultsSummaryEl = this.querySelector('[data-results-summary]')
    this.resultsTextEl = this.querySelector('[data-results-text]')
    this.activeFiltersEl = this.querySelector('[data-active-filters]')
    this.sheetEl = this._sheetPortaled || this.querySelector('[data-sheet]')
    this.sheetSearchInput = (this.sheetEl || this).querySelector('[data-sheet-search]')
    this.sheetPhotosOnlyInput = (this.sheetEl || this).querySelector('[data-sheet-photos-only]')
    this.sheetApplyBtn = (this.sheetEl || this).querySelector('[data-sheet-apply]')
    this.galleryEl = this.querySelector('tb-reviews-gallery')
    this.aiSummaryEl = this.querySelector('[data-ai-summary]')
    this.aiSummaryToggle = this.querySelector('[data-ai-summary-toggle]')
    this.summaryEl = this.querySelector('[data-summary]')
    this.summaryChevron = this.querySelector('[data-summary-chevron]')
    this.pillsEl = this.querySelector('[data-component-pills]')
    this.pillsFadeStart = this.querySelector('[data-pills-fade="start"]')
    this.pillsFadeEnd = this.querySelector('[data-pills-fade="end"]')
    this.setupAiSummary()
    this.setupPillsFade()
  }

  setupPillsFade () {
    if (!this.pillsEl) return

    if (!this.updatePillsFade) {
      this.updatePillsFade = () => {
        if (!this.pillsEl) return
        const max = this.pillsEl.scrollWidth - this.pillsEl.clientWidth
        const overflows = max > 1
        const atStart = this.pillsEl.scrollLeft <= 1
        const atEnd = this.pillsEl.scrollLeft >= max - 1
        if (this.pillsFadeStart) {
          this.pillsFadeStart.classList.toggle('product-reviews__pills-fade--visible', overflows && !atStart)
        }
        if (this.pillsFadeEnd) {
          this.pillsFadeEnd.classList.toggle('product-reviews__pills-fade--visible', overflows && !atEnd)
        }
      }
    }

    if (this._pillsScrollEl !== this.pillsEl) {
      if (this._pillsScrollEl) this._pillsScrollEl.removeEventListener('scroll', this.updatePillsFade)
      this._pillsScrollEl = this.pillsEl
      this.pillsEl.addEventListener('scroll', this.updatePillsFade, { passive: true })
    }

    if (typeof ResizeObserver !== 'undefined') {
      if (!this._pillsResizeObserver) {
        this._pillsResizeObserver = new ResizeObserver(() => this.updatePillsFade())
      }
      this._pillsResizeObserver.disconnect()
      this._pillsResizeObserver.observe(this.pillsEl)
      this.pillsEl.querySelectorAll('[data-component-pill]').forEach(pill => {
        this._pillsResizeObserver.observe(pill)
      })
    }

    this.updatePillsFade()
  }

  setupAiSummary () {
    if (!this.aiSummaryEl || !this.aiSummaryToggle) return
    this.applyAiSummaryClamp()
    if (!('ResizeObserver' in window)) return
    if (this._aiSummaryObserved === this.aiSummaryEl) return
    if (this._aiSummaryObserver) this._aiSummaryObserver.disconnect()
    this._aiSummaryObserver = new ResizeObserver(() => {
      if (this._aiSummaryFrame) return
      this._aiSummaryFrame = window.requestAnimationFrame(() => {
        this._aiSummaryFrame = null
        this.applyAiSummaryClamp()
      })
    })
    this._aiSummaryObserver.observe(this.aiSummaryEl)
    this._aiSummaryObserved = this.aiSummaryEl
  }

  applyAiSummaryClamp () {
    const text = this.aiSummaryEl
    const btn = this.aiSummaryToggle
    if (!text || !btn) return
    const labels = this.i18n || I18N_FALLBACKS
    text.classList.add(AI_SUMMARY_CLAMP)
    const overflows = text.scrollHeight > text.clientHeight + 1
    const expanded = this._aiSummaryExpanded === true && overflows
    text.classList.toggle(AI_SUMMARY_CLAMP, !expanded)
    btn.classList.toggle('hidden', !overflows)
    btn.textContent = expanded ? labels.readLess : labels.readMore
    btn.setAttribute('aria-expanded', expanded ? 'true' : 'false')
    this._aiSummaryExpanded = expanded
  }

  toggleSummary () {
    if (!this.summaryEl || !this.summaryChevron) return
    const collapsed = this.summaryEl.classList.toggle(SUMMARY_COLLAPSED)
    this.summaryChevron.setAttribute('aria-expanded', collapsed ? 'false' : 'true')
  }

  handleSectionLoad (event) {
    if (!this.state) return
    const section = this.closest('.shopify-section')
    if (!section || event.detail?.sectionId !== this.dataset.sectionId) return
    if (this._sheetPortaled) {
      this._sheetPortaled.remove()
      this._sheetPortaled = null
    }
    this.refreshDomRefs()
    this.render()
  }

  isDefaultState (state) {
    const s = state || this.state
    return !s.search && s.component === 'all' && !s.photosOnly && s.sort === 'recent'
  }

  isServerMode () {
    return !this.isDefaultState()
  }

  loadVotedCounts () {
    try {
      const raw = window.localStorage.getItem(`tb-reviews-voted:${this.store}`)
      const parsed = raw ? JSON.parse(raw) : {}
      if (Array.isArray(parsed)) {
        const migrated = {}
        for (const id of parsed) migrated[String(id)] = 1
        return migrated
      }
      return parsed && typeof parsed === 'object' ? parsed : {}
    } catch {
      return {}
    }
  }

  persistVotedCounts () {
    try {
      window.localStorage.setItem(`tb-reviews-voted:${this.store}`, JSON.stringify(this.votedCounts))
    } catch {
      this._voteStorageBlocked = true
    }
  }

  bindControlEvents () {
    if (this._controlEventsBound) return
    this._controlEventsBound = true

    this.addEventListener('input', (event) => {
      const searchInput = event.target.closest('[data-search-input]')
      if (searchInput && this.contains(searchInput)) {
        window.clearTimeout(this._searchTimer)
        this._searchTimer = window.setTimeout(() => {
          this.applyState({ search: searchInput.value.trim() })
        }, 300)
        return
      }

      const sheetSearch = event.target.closest('[data-sheet-search]')
      if (sheetSearch && this.contains(sheetSearch)) {
        this.draft.search = sheetSearch.value.trim()
        this.scheduleSheetCount()
      }
    })

    this.addEventListener('change', (event) => {
      const photosOnly = event.target.closest('[data-photos-only]')
      if (photosOnly && this.contains(photosOnly)) {
        this.applyState({ photosOnly: photosOnly.checked })
        return
      }

      const sheetPhotosOnly = event.target.closest('[data-sheet-photos-only]')
      if (sheetPhotosOnly && this.contains(sheetPhotosOnly)) {
        this.draft.photosOnly = sheetPhotosOnly.checked
        this.scheduleSheetCount()
        return
      }

      const sortSelect = event.target.closest('[data-sort-select]')
      if (sortSelect && this.contains(sortSelect)) {
        this.applyState({ sort: sortSelect.value || 'recent' })
      }
    })

    this.addEventListener('click', (event) => {
      const componentPill = event.target.closest('[data-component-pill]')
      if (componentPill && this.contains(componentPill)) {
        this.applyState({ component: componentPill.dataset.componentPill || 'all' })
        return
      }

      const clearSearch = event.target.closest('[data-clear-search]')
      if (clearSearch && this.contains(clearSearch)) {
        this.applyState({ search: '' })
        return
      }

      const resetBtn = event.target.closest('[data-reset]')
      if (resetBtn && this.contains(resetBtn)) {
        this.applyState({ ...DEFAULT_STATE })
        return
      }

      const loadMoreBtn = event.target.closest('[data-load-more]')
      if (loadMoreBtn && this.contains(loadMoreBtn)) {
        this.handleLoadMore()
        return
      }

      const readMoreBtn = event.target.closest('[data-read-more]')
      if (readMoreBtn && this.contains(readMoreBtn)) {
        const content = readMoreBtn.parentElement.querySelector('[data-review-content]')
        if (content) {
          const expanded = !content.classList.contains('line-clamp-6')
          content.classList.toggle('line-clamp-6', expanded)
          readMoreBtn.textContent = expanded ? this.i18n.readMore : this.i18n.readLess
          readMoreBtn.setAttribute('aria-expanded', expanded ? 'false' : 'true')
        }
        return
      }

      const aiSummaryToggle = event.target.closest('[data-ai-summary-toggle]')
      if (aiSummaryToggle && this.contains(aiSummaryToggle)) {
        this._aiSummaryExpanded = !this._aiSummaryExpanded
        this.applyAiSummaryClamp()
        return
      }

      const summaryToggle = event.target.closest('[data-summary-toggle]')
      if (summaryToggle && this.contains(summaryToggle) && !this.isDesktop()) {
        this.toggleSummary()
        return
      }

      const helpfulBtn = event.target.closest('[data-helpful]')
      if (helpfulBtn && this.contains(helpfulBtn)) {
        this.handleHelpfulVote(helpfulBtn)
        return
      }

      const galleryOpen = event.target.closest('[data-gallery-open]')
      if (galleryOpen && this.contains(galleryOpen)) {
        this.openGallery({ view: 'grid' })
        return
      }

      const stripPhoto = event.target.closest('[data-strip-photo]')
      if (stripPhoto && this.contains(stripPhoto)) {
        this.openGallery({
          view: this.isDesktop() ? 'detail' : 'lightbox',
          photoFull: stripPhoto.dataset.photoFull || '',
          review: this.findReviewById(stripPhoto.dataset.reviewRef || ''),
        })
        return
      }

      const chipRemove = event.target.closest('[data-chip-remove]')
      if (chipRemove && this.contains(chipRemove)) {
        this.removeChip(chipRemove.dataset.chipRemove)
        return
      }

      const clearAll = event.target.closest('[data-clear-all]')
      if (clearAll && this.contains(clearAll)) {
        this.applyState({ ...DEFAULT_STATE })
        return
      }

      const sheetOpen = event.target.closest('[data-sheet-open]')
      if (sheetOpen && this.contains(sheetOpen)) {
        this.openSheet()
        return
      }

      const sheetClose = event.target.closest('[data-sheet-close]')
      if (sheetClose && this.contains(sheetClose)) {
        this.closeSheet()
        return
      }

      const sheetSortPill = event.target.closest('[data-sheet-sort-pill]')
      if (sheetSortPill && this.contains(sheetSortPill)) {
        this.draft.sort = sheetSortPill.dataset.sheetSortPill || 'recent'
        this.syncSheetUi()
        this.scheduleSheetCount()
        return
      }

      const sheetComponentPill = event.target.closest('[data-sheet-component-pill]')
      if (sheetComponentPill && this.contains(sheetComponentPill)) {
        this.draft.component = sheetComponentPill.dataset.sheetComponentPill || 'all'
        this.syncSheetUi()
        this.scheduleSheetCount()
        return
      }

      const sheetReset = event.target.closest('[data-sheet-reset]')
      if (sheetReset && this.contains(sheetReset)) {
        this.draft = { ...DEFAULT_STATE }
        this.syncSheetUi()
        this.scheduleSheetCount()
        return
      }

      const sheetApply = event.target.closest('[data-sheet-apply]')
      if (sheetApply && this.contains(sheetApply)) {
        this.closeSheet()
        this.applyState({ ...this.draft })
      }
    })
  }

  isDesktop () {
    return window.matchMedia('(min-width: 768px)').matches
  }

  applyState (patch) {
    const filterKeys = ['search', 'component', 'photosOnly', 'sort']
    const filtersChanged = filterKeys.some(key => key in patch && patch[key] !== this.state[key])
    Object.assign(this.state, patch)
    this.draft = {
      search: this.state.search,
      component: this.state.component,
      photosOnly: this.state.photosOnly,
      sort: this.state.sort,
    }

    if (filtersChanged) {
      this.state.visible = this.initialCount
      if (this.isServerMode() && this.canUseApi) {
        this.state.serverRows = []
        this.state.serverCursor = null
        this.state.serverExhausted = false
        this.state.serverTotal = null
        this.fetchServerPage({ initial: true })
      }
    }

    this.syncControlsUi()
    this.render()
  }

  removeChip (kind) {
    if (kind === 'search') this.applyState({ search: '' })
    else if (kind === 'sort') this.applyState({ sort: 'recent' })
    else if (kind === 'photos') this.applyState({ photosOnly: false })
  }

  syncControlsUi () {
    if (this.searchInput && this.searchInput.value.trim() !== this.state.search) {
      this.searchInput.value = this.state.search
    }
    if (this.photosOnlyInput) this.photosOnlyInput.checked = this.state.photosOnly
    if (this.sortSelect) this.sortSelect.value = this.state.sort
    this.querySelectorAll('[data-component-pill]').forEach(pill => {
      const active = (pill.dataset.componentPill || 'all') === this.state.component
      pill.classList.toggle('product-reviews__pill--active', active)
      pill.classList.toggle('bg-black', active)
      pill.classList.toggle('text-white', active)
      pill.classList.toggle('bg-white', !active)
      pill.classList.toggle('text-black', !active)
      pill.classList.toggle('border', !active)
      pill.classList.toggle('border-clay', !active)
      pill.setAttribute('aria-pressed', active ? 'true' : 'false')
    })
    this.syncSheetUi()
    this.renderActiveChips()
  }

  syncSheetUi () {
    if (this.sheetSearchInput && this.sheetSearchInput.value.trim() !== this.draft.search) {
      this.sheetSearchInput.value = this.draft.search
    }
    if (this.sheetPhotosOnlyInput) this.sheetPhotosOnlyInput.checked = this.draft.photosOnly
    const syncPills = (selector, datasetKey, current) => {
      const scope = this.sheetEl || this
      scope.querySelectorAll(selector).forEach(pill => {
        const active = (pill.dataset[datasetKey] || '') === current
        pill.classList.toggle('product-reviews__pill--active', active)
        pill.classList.toggle('bg-black', active)
        pill.classList.toggle('text-white', active)
        pill.classList.toggle('bg-white', !active)
        pill.classList.toggle('border', !active)
        pill.classList.toggle('border-clay', !active)
        pill.setAttribute('aria-pressed', active ? 'true' : 'false')
      })
    }
    syncPills('[data-sheet-sort-pill]', 'sheetSortPill', this.draft.sort)
    syncPills('[data-sheet-component-pill]', 'sheetComponentPill', this.draft.component)
  }

  renderActiveChips () {
    if (!this.activeFiltersEl) return
    const chips = []
    if (this.state.search) {
      chips.push({ kind: 'search', label: `"${this.state.search}"` })
    }
    if (this.state.sort !== 'recent') {
      chips.push({ kind: 'sort', label: this.i18n.sortLabels[this.state.sort] || this.state.sort })
    }
    if (this.state.photosOnly) {
      chips.push({ kind: 'photos', label: this.i18n.photosOnlyLabel })
    }

    if (!chips.length) {
      this.activeFiltersEl.classList.add('hidden')
      this.activeFiltersEl.classList.remove('flex')
      this.activeFiltersEl.innerHTML = ''
      return
    }

    const removeAria = escapeHtml(this.i18n.removeFilterAria)
    const chipHtml = chips.map(chip => `
      <button type="button" class="product-reviews__chip inline-flex items-center gap-1.5 pl-3 pr-2.5 py-1.75 rounded-full bg-black text-white text-smol font-medium" data-chip-remove="${chip.kind}" aria-label="${removeAria}">
        ${escapeHtml(chip.label)}
        <span class="tb-icon inline-block w-3.5 h-3.5" aria-hidden="true"><svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" class="w-full h-full" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg></span>
      </button>`).join('')

    const clearAllHtml = `
      <button type="button" class="inline-flex items-center gap-1 text-base" data-clear-all>
        ${escapeHtml(this.i18n.clearAll)}
        <span class="tb-icon inline-block w-4 h-4" aria-hidden="true"><svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" class="w-full h-full" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"/></svg></span>
      </button>`

    this.activeFiltersEl.innerHTML = chipHtml + clearAllHtml
    this.activeFiltersEl.classList.remove('hidden')
    this.activeFiltersEl.classList.add('flex')
  }

  openSheet () {
    if (!this.sheetEl) return
    if (this.sheetEl.parentElement !== document.body) {
      document.body.appendChild(this.sheetEl)
      this._sheetPortaled = this.sheetEl
      this.bindSheetEvents(this.sheetEl)
    }
    this._sheetReturnFocus = document.activeElement
    this.draft = {
      search: this.state.search,
      component: this.state.component,
      photosOnly: this.state.photosOnly,
      sort: this.state.sort,
    }
    this.syncSheetUi()
    this.updateSheetCount(this.currentTotal())
    this.sheetEl.classList.remove('hidden')
    this.sheetEl.setAttribute('aria-hidden', 'false')
    window.dispatchEvent(new CustomEvent('show:page-obscure'))
    window.dispatchEvent(new CustomEvent('prevent:page-scroll'))
    window.requestAnimationFrame(() => {
      this.sheetEl.classList.add('product-reviews__sheet--open')
      this.sheetEl.querySelector('[data-sheet-close]')?.focus({ preventScroll: true })
    })
  }

  closeSheet () {
    if (!this.sheetEl) return
    this.sheetEl.classList.remove('product-reviews__sheet--open')
    this.sheetEl.classList.add('hidden')
    this.sheetEl.setAttribute('aria-hidden', 'true')
    window.dispatchEvent(new CustomEvent('hide:page-obscure'))
    window.dispatchEvent(new CustomEvent('allow:page-scroll'))
    if (this._sheetReturnFocus && typeof this._sheetReturnFocus.focus === 'function') {
      this._sheetReturnFocus.focus({ preventScroll: true })
    }
  }

  scheduleSheetCount () {
    window.clearTimeout(this._sheetCountTimer)
    this._sheetCountTimer = window.setTimeout(() => this.fetchSheetCount(), 400)
  }

  draftKey (draft) {
    return JSON.stringify([draft.search, draft.component, draft.photosOnly, draft.sort])
  }

  async fetchSheetCount () {
    const draft = { ...this.draft }
    if (this.isDefaultState(draft)) {
      this.updateSheetCount(this.total)
      return
    }
    if (!this.canUseApi) {
      this.updateSheetCount(this.localFilteredRows(draft).length)
      return
    }

    const key = this.draftKey(draft)
    const cached = this._countCache.get(key)
    if (cached) {
      this.updateSheetCount(cached.total)
      return
    }

    try {
      const json = await this.fetchApi(this.buildApiUrl(draft, null))
      this._countCache.set(key, { total: json.total ?? 0, json })
      if (this.draftKey(this.draft) === key) {
        this.updateSheetCount(json.total ?? 0)
      }
    } catch (err) {
      console.warn('[tb-product-reviews] count prefetch failed', err)
    }
  }

  updateSheetCount (count) {
    if (!this.sheetApplyBtn) return
    const safeCount = Number.isFinite(count) ? count : this.total
    const template = safeCount === 1 ? this.i18n.sheetShowSingular : this.i18n.sheetShow
    this.sheetApplyBtn.textContent = interpolate(template, { count: safeCount })
  }

  currentTotal () {
    if (this.isServerMode()) {
      if (!this.canUseApi) return this.localFilteredRows().length
      return this.state.serverTotal ?? this.getRows().length
    }
    return this.total
  }

  localFilteredRows (filters = this.state) {
    const byKey = new Map()
    for (const review of [...this.seedReviews, ...this.apiReviews]) {
      byKey.set(reviewOrderKey(review), review)
    }
    let rows = Array.from(byKey.values())
    if (filters.component && filters.component !== 'all') {
      const target = String(filters.component).toLowerCase()
      rows = rows.filter(r => {
        const comp = String(r.bundle_component_title || '').toLowerCase()
        return filters.component === 'self' ? !comp : comp === target
      })
    }
    if (filters.photosOnly) {
      rows = rows.filter(r => Array.isArray(r.images) && r.images.length > 0)
    }
    if (filters.search) {
      const q = String(filters.search).toLowerCase()
      rows = rows.filter(r => String(r.title || '').toLowerCase().includes(q) || String(r.content || '').toLowerCase().includes(q))
    }
    const byCreatedDesc = (a, b) => String(b.created || '').localeCompare(String(a.created || ''))
    if (filters.sort === 'helpful') {
      rows.sort((a, b) => ((Number(b.helpful_count) || 0) - (Number(a.helpful_count) || 0)) || byCreatedDesc(a, b))
    } else if (filters.sort === 'rating-desc') {
      rows.sort((a, b) => (reviewRating(b) - reviewRating(a)) || byCreatedDesc(a, b))
    } else if (filters.sort === 'rating-asc') {
      rows.sort((a, b) => (reviewRating(a) - reviewRating(b)) || byCreatedDesc(a, b))
    }
    return rows
  }

  getRows () {
    if (this.isServerMode()) {
      if (!this.canUseApi) return this.localFilteredRows()
      return this.state.serverRows
    }
    const byKey = new Map()
    for (const review of [...this.seedReviews, ...this.apiReviews]) {
      byKey.set(reviewOrderKey(review), review)
    }
    return Array.from(byKey.values())
  }

  render () {
    if (!this.listEl) return
    const rows = this.getRows()
    const visibleCount = Math.min(rows.length, this.state.visible)
    const slice = rows.slice(0, visibleCount)

    if (this.state.loading && slice.length === 0) {
      this.listEl.innerHTML = `<div class="product-reviews__loading text-center text-iron py-6">${escapeHtml(this.i18n.loadingList)}</div>`
    } else {
      this.listEl.innerHTML = slice.map(r => this.renderCard(r)).join('')
      this.applyReadMore()
    }

    const isEmpty = rows.length === 0 && !this.state.loading && this.isServerMode() && (!this.canUseApi || this.state.serverTotal === 0)
    this.showEmpty(isEmpty)
    this.renderResultsSummary()

    let remaining = 0
    let showLoadMore = false
    if (this.isServerMode()) {
      const totalKnown = this.canUseApi ? this.state.serverTotal : rows.length
      remaining = totalKnown !== null ? Math.max(0, totalKnown - visibleCount) : 0
      showLoadMore = rows.length > visibleCount || (this.canUseApi && !this.state.serverExhausted)
    } else {
      const clientRemaining = rows.length - visibleCount
      remaining = Math.max(0, this.total - visibleCount)
      showLoadMore = clientRemaining > 0 || (this.canPaginateApi && !this.state.apiExhausted)
    }

    if (this.loadMoreBtn) {
      this.loadMoreBtn.hidden = !showLoadMore
      if (this.loadMoreCountEl) {
        this.loadMoreCountEl.textContent = remaining > 0 ? interpolate(this.i18n.loadMoreCount, { count: remaining }) : ''
      }
      if (this.loadMoreLabelEl) {
        this.loadMoreLabelEl.textContent = this.state.loading ? this.i18n.loadingButton : this.loadMoreDefaultLabel
      }
      this.loadMoreBtn.disabled = !!this.state.loading
    }
  }

  applyReadMore () {
    if (!this.listEl) return
    this.listEl.querySelectorAll('[data-review-content]').forEach(content => {
      const btn = content.parentElement.querySelector('[data-read-more]')
      if (!btn) return
      if (content.classList.contains('line-clamp-6') && content.scrollHeight > content.clientHeight + 1) {
        btn.classList.remove('hidden')
      } else if (content.classList.contains('line-clamp-6')) {
        btn.classList.add('hidden')
      }
    })
  }

  renderResultsSummary () {
    if (!this.resultsSummaryEl || !this.resultsTextEl) return
    if (this.state.search && (!this.canUseApi || this.state.serverTotal !== null)) {
      const count = this.currentTotal()
      const template = count === 1 ? this.i18n.resultsSummarySingular : this.i18n.resultsSummary
      const text = interpolate(template, { count, term: this.state.search })
      const escapedTerm = escapeHtml(this.state.search)
      const html = escapeHtml(text).replace(
        `&quot;${escapedTerm}&quot;`,
        `<span class="font-semibold text-black">&quot;${escapedTerm}&quot;</span>`,
      )
      this.resultsTextEl.innerHTML = html
      this.resultsSummaryEl.classList.remove('hidden')
      this.resultsSummaryEl.classList.add('flex')
    } else {
      this.resultsSummaryEl.classList.add('hidden')
      this.resultsSummaryEl.classList.remove('flex')
    }
  }

  showEmpty (show) {
    if (!this.emptyEl) return
    if (show) {
      this.emptyEl.classList.remove('hidden')
      this.emptyEl.classList.add('flex', 'flex-col', 'items-center')
    } else {
      this.emptyEl.classList.add('hidden')
      this.emptyEl.classList.remove('flex', 'flex-col', 'items-center')
    }
  }

  renderError (err) {
    console.error('[tb-product-reviews] load failed', err)
    this.state.loading = false
    if (this.listEl) {
      this.listEl.innerHTML = `<div class="product-reviews__error text-center text-iron py-6">${escapeHtml(this.i18n.errorLabel)} <button type="button" class="underline hover:text-black" data-retry>${escapeHtml(this.i18n.errorRetry)}</button></div>`
      const retry = this.listEl.querySelector('[data-retry]')
      if (retry) {
        retry.addEventListener('click', () => {
          if (this.isServerMode()) {
            this.fetchServerPage({ initial: this.state.serverRows.length === 0 })
          } else {
            this.render()
          }
        })
      }
    }
  }

  async handleLoadMore () {
    if (this.isServerMode()) {
      if (!this.canUseApi) {
        const localRows = this.getRows()
        this.state.visible = Math.min(localRows.length, this.state.visible + this.pageSize)
        this.render()
        return
      }
      const rows = this.state.serverRows
      const shown = Math.min(rows.length, this.state.visible)
      if (rows.length > shown) {
        this.state.visible = Math.min(rows.length, shown + this.pageSize)
        this.render()
        if (!this.state.serverExhausted && rows.length - this.state.visible < this.pageSize) {
          this.fetchServerPage({})
        }
        return
      }
      if (!this.state.serverExhausted) {
        this.state.loading = true
        this.render()
        const applied = await this.fetchServerPage({})
        this.state.loading = false
        if (applied) {
          this.state.visible += this.pageSize
        }
        this.render()
      }
      return
    }

    const rows = this.getRows()
    const currentlyShown = Math.min(rows.length, this.state.visible)
    const clientHasMore = rows.length > currentlyShown

    if (clientHasMore) {
      this.state.visible = Math.min(rows.length, currentlyShown + this.pageSize)
      this.render()
      const remaining = rows.length - this.state.visible
      if (this.canPaginateApi && !this.state.apiExhausted && remaining < this.pageSize) {
        try { await this.fetchApiPage() } catch (err) { console.warn('[tb-product-reviews] prefetch failed', err) }
        this.render()
      }
      return
    }

    if (this.canPaginateApi && !this.state.apiExhausted) {
      this.state.loading = true
      this.render()
      try {
        await this.fetchApiPage()
        this.state.visible += this.pageSize
      } catch (err) {
        this.renderError(err)
        return
      } finally {
        this.state.loading = false
      }
      this.render()
    }
  }

  buildApiUrl (filters, cursor) {
    const params = new URLSearchParams()
    params.set('store', this.store)
    params.set('limit', String(Math.max(this.pageSize, 24)))
    params.set('sort', filters.sort || 'recent')

    if (cursor) params.set('cursor', cursor)
    if (filters.search) params.set('q', filters.search)
    if (filters.component && filters.component !== 'all') params.set('component', filters.component)
    if (filters.photosOnly) params.set('with_photos', '1')

    const isDefault = this.isDefaultState({ ...DEFAULT_STATE, ...filters })
    if (!cursor && isDefault && this.knownIds.size > 0) {
      const ids = Array.from(this.knownIds).slice(0, 200).join(',')
      if (ids) params.set('exclude_ids', ids)
    }

    return `${this.appUrl}/api/storefront/klaviyo-reviews/${encodeURIComponent(this.productId)}?${params.toString()}`
  }

  async fetchApi (url) {
    const res = await fetch(url, {
      headers: { Accept: 'application/json' },
      credentials: 'omit',
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const contentType = res.headers.get('content-type') || ''
    if (!contentType.includes('application/json')) throw new Error('Non-JSON response')
    return res.json()
  }

  async fetchServerPage ({ initial = false } = {}) {
    if (!this.canUseApi) return false
    const seq = ++this._fetchSeq
    const filters = {
      search: this.state.search,
      component: this.state.component,
      photosOnly: this.state.photosOnly,
      sort: this.state.sort,
    }
    const cursor = initial ? null : this.state.serverCursor

    if (initial) {
      this.state.loading = true
      this.render()
    }

    try {
      const cacheKey = this.draftKey(filters)
      let json = null
      if (initial) {
        const cached = this._countCache.get(cacheKey)
        if (cached?.json) json = cached.json
      }
      if (!json) {
        json = await this.fetchApi(this.buildApiUrl(filters, cursor))
        if (initial) this._countCache.set(cacheKey, { total: json.total ?? 0, json })
      }
      if (seq !== this._fetchSeq) return false
      const incoming = Array.isArray(json.reviews) ? json.reviews : []
      if (initial) this.state.serverRows = []
      const seen = new Set(this.state.serverRows.map(r => reviewOrderKey(r)))
      for (const r of incoming) {
        const key = reviewOrderKey(r)
        if (!seen.has(key)) {
          seen.add(key)
          this.state.serverRows.push(r)
        }
      }
      this.state.serverTotal = Number.isFinite(json.total) ? json.total : this.state.serverRows.length
      this.state.serverCursor = json.next_cursor || null
      this.state.serverExhausted = !this.state.serverCursor
      if (initial) this.state.loading = false
      this.render()
      return true
    } catch (err) {
      if (seq !== this._fetchSeq) return false
      this.renderError(err)
      return false
    }
  }

  async fetchApiPage () {
    if (this.state.apiExhausted || !this.canPaginateApi) return
    const json = await this.fetchApi(this.buildApiUrl(DEFAULT_STATE, this.state.apiCursor))
    const incoming = Array.isArray(json.reviews) ? json.reviews : []
    for (const r of incoming) {
      if (!this.knownIds.has(r.id)) {
        this.knownIds.add(r.id)
        this.apiReviews.push(r)
      }
    }
    this.state.apiCursor = json.next_cursor || null
    if (!this.state.apiCursor) this.state.apiExhausted = true
  }

  componentProductFor (title) {
    if (!title) return null
    return this.componentProducts[String(title).trim().toLowerCase()] || null
  }

  helpfulLabelHtml (review) {
    const count = Number(review.helpful_count) || 0
    const desktopCount = count === 1
      ? interpolate(this.i18n.helpfulCountSingular, { count })
      : interpolate(this.i18n.helpfulCount, { count })
    const mobileCount = interpolate(this.i18n.helpfulCountMobile, { count })
    return `<span class="hidden md:inline">${escapeHtml(this.i18n.helpfulPrompt)} · ${escapeHtml(desktopCount)}</span><span class="md:hidden">${escapeHtml(mobileCount)}</span>`
  }

  originFooterHtml (review, author) {
    const origin = (review.origin_store || '').toLowerCase()
    const escapedAuthor = escapeHtml(author)

    if (origin && origin !== this.homeOrigin && this.i18n.originSites[origin]) {
      const flagUrl = this.flags[origin]
      const text = interpolate(this.i18n.originWebsite, { author, site: this.i18n.originSites[origin] })
      const flagHtml = flagUrl
        ? `<img src="${escapeHtml(flagUrl)}" alt="" width="16" height="16" loading="lazy" class="inline-block w-4 h-4 rounded-full">`
        : ''
      return `<span class="product-reviews__card-origin inline-flex items-center gap-2">${flagHtml}<span class="product-reviews__card-author">${escapeHtml(text)}</span></span>`
    }

    const homeCountry = HOME_COUNTRIES[this.homeOrigin]
    const country = (review.country || '').toUpperCase()
    if (country && homeCountry && country !== homeCountry) {
      const name = countryName(country, this.locale)
      if (name) {
        const flagUrl = this.flags[country.toLowerCase()]
        const text = interpolate(this.i18n.originCountry, { author, country: name })
        const flagHtml = flagUrl
          ? `<img src="${escapeHtml(flagUrl)}" alt="" width="16" height="16" loading="lazy" class="inline-block w-4 h-4 rounded-full">`
          : ''
        return `<span class="product-reviews__card-origin inline-flex items-center gap-2">${flagHtml}<span class="product-reviews__card-author">${escapeHtml(text)}</span></span>`
      }
    }

    return `<span class="product-reviews__card-author">${escapedAuthor}</span>`
  }

  renderCard (review) {
    const term = this.state.search
    const full = Math.floor(reviewRating(review))
    const titleHtml = review.title
      ? `<h3 class="product-reviews__card-title text-lg font-semibold">${highlightHtml(review.title, term)}</h3>`
      : ''
    const contentHtml = review.content
      ? `<div class="product-reviews__card-body flex flex-col items-start gap-1"><p class="product-reviews__card-content text-iron leading-normal whitespace-pre-line line-clamp-6" data-review-content>${highlightHtml(review.content, term)}</p><button type="button" class="product-reviews__read-more hidden text-sm text-black underline" data-read-more aria-expanded="false">${escapeHtml(this.i18n.readMore)}</button></div>`
      : ''

    let verifiedBadge = ''
    if (review.gifted === true) {
      verifiedBadge = `<span class="product-reviews__card-badge product-reviews__card-badge--gifted select-none inline-flex items-center text-xs leading-[1.1] text-iron bg-oat border border-clay rounded-full px-2 py-1">${escapeHtml(this.i18n.giftedProduct)}</span>`
    } else if (review.verified) {
      verifiedBadge = `<span class="product-reviews__card-badge select-none inline-flex items-center gap-1 text-xs text-white bg-success rounded-full px-2 py-1"><span class="tb-icon inline-block w-3 h-3">${CHECK_ICON}</span>${escapeHtml(this.i18n.verifiedBuyer)}</span>`
    }

    const componentTitle = review.bundle_component_title || ''
    const componentProduct = this.componentProductFor(componentTitle)

    let tagHtml = ''
    let componentCardHtml = ''
    if (componentProduct) {
      const imageHtml = componentProduct.image
        ? `<img src="${escapeHtml(componentProduct.image)}" alt="" width="96" height="96" loading="lazy" class="w-12 h-12 rounded object-cover">`
        : ''
      componentCardHtml = `<a href="${escapeHtml(componentProduct.url || '#')}" class="product-reviews__card-component inline-flex items-center gap-4 pl-2 pr-4 py-2 bg-white border border-clay rounded-lg">${imageHtml}<span class="flex flex-col items-start"><span class="text-xs text-iron leading-[1.1]">${escapeHtml(this.i18n.reviewFor)}</span><span class="text-sm font-semibold">${escapeHtml(componentTitle)}</span></span></a>`
    } else if (this.hasComponents) {
      const tagLabel = componentTitle || this.productTitle
      if (tagLabel) {
        tagHtml = `<span class="product-reviews__card-tag inline-flex items-center justify-center p-1 text-xs bg-white border border-clay rounded">${escapeHtml(tagLabel)}</span>`
      }
    }

    let sourceBadges = ''
    if (Array.isArray(review.badges)) {
      sourceBadges = review.badges.map(badge => {
        const label = String(badge).replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
        const text = interpolate(this.i18n.sourceBadge, { source: label })
        return `<span class="product-reviews__card-badge product-reviews__card-badge--source inline-flex items-center gap-1 text-xs text-iron bg-oat rounded-full px-2 py-0.5">${escapeHtml(text)}</span>`
      }).join('')
    }

    let imagesHtml = ''
    if (Array.isArray(review.images) && review.images.length > 0) {
      const openPhotoAria = escapeHtml(this.i18n.openPhotoAria)
      imagesHtml = `<div class="product-reviews__card-images flex flex-wrap gap-2">${review.images.map(img => {
        const thumb = img && typeof img === 'object' && img.thumb ? img.thumb : ''
        const fullImage = img && typeof img === 'object' && img.full ? img.full : thumb
        if (!thumb) return ''
        return `<a href="${escapeHtml(fullImage)}" class="product-reviews__card-image relative block w-20 h-20 rounded overflow-hidden focus:outline-hidden focus:ring-2 focus:ring-black" data-image-src="${escapeHtml(fullImage)}" target="_blank" rel="noopener" aria-label="${openPhotoAria}"><img src="${escapeHtml(thumb)}" alt="" width="300" height="300" loading="lazy" decoding="async" class="product-reviews__card-image-thumb w-full h-full object-cover">${PHOTO_SPINNER_HTML}</a>`
      }).join('')}</div>`
    }

    const voted = this.votedIds.has(String(review.id))
    let helpfulCount = Number(review.helpful_count) || 0
    if (voted) helpfulCount = Math.max(helpfulCount, Number(this.votedCounts[String(review.id)]) || 1)
    const helpfulAria = voted ? this.i18n.helpfulVotedAria : this.i18n.helpfulVoteAria
    const helpfulHtml = `<button type="button" class="product-reviews__helpful inline-flex items-center gap-1.5 self-start text-smol text-iron${voted ? ' product-reviews__helpful--voted' : ''}" data-helpful data-helpful-count="${helpfulCount}" aria-label="${escapeHtml(helpfulAria)}"${voted ? ' disabled' : ''}><span class="tb-icon inline-block w-3.5 h-3.5" data-helpful-icon aria-hidden="true">${voted ? HEART_ICON_FILLED : HEART_ICON}</span><span data-helpful-label>${this.helpfulLabelHtml({ helpful_count: helpfulCount })}</span></button>` // eslint-disable-line camelcase

    let replyHtml = ''
    if (review.public_reply && review.public_reply.content) {
      const replyAuthor = review.public_reply.author || this.i18n.defaultReplyAuthor
      replyHtml = `<div class="product-reviews__card-reply p-3 rounded bg-oat text-sm"><div class="product-reviews__card-reply-author font-semibold mb-1">${escapeHtml(replyAuthor)}</div><p class="product-reviews__card-reply-content whitespace-pre-line">${escapeHtml(review.public_reply.content)}</p></div>`
    }

    const author = review.author || this.i18n.defaultAuthor
    const dateText = formatDate(review.created)
    const starsAria = interpolate(this.i18n.starsAria, { rating: reviewRating(review) })

    return `<article class="product-reviews__card flex flex-col gap-4 rounded-lg border border-clay p-5 md:p-8 bg-white" data-review-id="${escapeHtml(review.id)}" data-review-rating="${reviewRating(review)}" data-review-has-photos="${Array.isArray(review.images) && review.images.length > 0}" data-review-verified="${review.verified === true}" data-review-gifted="${review.gifted === true}">
      <header class="product-reviews__card-header flex flex-wrap items-center gap-2 md:gap-3${componentCardHtml ? ' justify-between' : ''}">
        <div class="product-reviews__card-header-left flex flex-wrap items-center gap-2 md:gap-3">
          <div class="product-reviews__card-stars flex items-center gap-0.5 text-gold h-4.5" role="img" aria-label="${escapeHtml(starsAria)}">
            ${starRowHtml(full)}
          </div>
          ${verifiedBadge}
          ${tagHtml}
          ${sourceBadges}
        </div>
        ${componentCardHtml}
      </header>
      ${titleHtml}
      ${contentHtml}
      ${imagesHtml}
      ${helpfulHtml}
      <footer class="product-reviews__card-footer flex items-center justify-between text-xs text-iron">
        ${this.originFooterHtml(review, author)}
        <time datetime="${escapeHtml(review.created || '')}" class="product-reviews__card-date">${dateText}</time>
      </footer>
      ${replyHtml}
    </article>`
  }

  findReviewById (id) {
    const key = String(id)
    return this.getRows().find(r => String(r.id) === key) ||
      this.seedReviews.find(r => String(r.id) === key) ||
      this.apiReviews.find(r => String(r.id) === key) ||
      this.state.serverRows.find(r => String(r.id) === key) ||
      null
  }

  async handleHelpfulVote (button) {
    const card = button.closest('[data-review-id]')
    if (!card) return
    const reviewId = card.dataset.reviewId
    if (!reviewId || this.votedIds.has(String(reviewId))) return

    const review = this.findReviewById(reviewId)
    const previousCount = Number(button.dataset.helpfulCount) || Number(review?.helpful_count) || 0

    this.votedIds.add(String(reviewId))
    this.votedCounts[String(reviewId)] = previousCount + 1
    this.persistVotedCounts()
    if (review) review.helpful_count = previousCount + 1 // eslint-disable-line camelcase
    this.updateHelpfulButton(button, previousCount + 1, true)

    if (!this.canUseApi) return

    try {
      const res = await fetch(`${this.appUrl}/api/storefront/klaviyo-reviews/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'omit',
        body: JSON.stringify({ review_id: reviewId }), // eslint-disable-line camelcase
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const json = await res.json()
      if (Number.isFinite(json.helpful_count)) {
        if (review) review.helpful_count = json.helpful_count // eslint-disable-line camelcase
        this.votedCounts[String(reviewId)] = json.helpful_count
        this.persistVotedCounts()
        this.updateHelpfulButton(button, json.helpful_count, true)
      }
    } catch (err) {
      console.warn('[tb-product-reviews] vote failed', err)
      this.votedIds.delete(String(reviewId))
      delete this.votedCounts[String(reviewId)]
      this.persistVotedCounts()
      if (review) review.helpful_count = previousCount // eslint-disable-line camelcase
      this.updateHelpfulButton(button, previousCount, false)
    }
  }

  updateHelpfulButton (button, count, voted) {
    if (!button || !button.isConnected) return
    button.dataset.helpfulCount = String(count)
    button.classList.toggle('product-reviews__helpful--voted', voted)
    button.disabled = voted
    button.setAttribute('aria-label', voted ? this.i18n.helpfulVotedAria : this.i18n.helpfulVoteAria)
    const icon = button.querySelector('[data-helpful-icon]')
    if (icon) icon.innerHTML = voted ? HEART_ICON_FILLED : HEART_ICON
    const label = button.querySelector('[data-helpful-label]')
    if (label) label.innerHTML = this.helpfulLabelHtml({ helpful_count: count }) // eslint-disable-line camelcase
  }

  openGallery (options = {}) {
    if (!this.galleryEl) this.galleryEl = this.querySelector('tb-reviews-gallery')
    if (!this.galleryEl || typeof this.galleryEl.openGallery !== 'function') return
    this.galleryEl.openGallery(options)
  }

  showReview (review) {
    if (!review) return
    if (!this.isDefaultState()) {
      this.state = {
        ...this.state,
        ...DEFAULT_STATE,
        visible: this.initialCount,
        serverRows: [],
        serverCursor: null,
        serverExhausted: false,
        serverTotal: null,
      }
      this.syncControlsUi()
    }
    const key = reviewOrderKey(review)
    let rows = this.getRows()
    let index = rows.findIndex(r => reviewOrderKey(r) === key)
    if (index === -1) {
      if (review.id) this.knownIds.add(review.id)
      this.apiReviews.push(review)
      rows = this.getRows()
      index = rows.findIndex(r => reviewOrderKey(r) === key)
    }
    if (index >= this.state.visible) {
      this.state.visible = index + 1
    }
    this.render()
    window.requestAnimationFrame(() => {
      const card = this.querySelector(`[data-review-id="${CSS.escape(String(review.id || ''))}"]`)
      if (card) card.scrollIntoView({ behavior: 'smooth', block: 'center' })
    })
  }

  handleHostClick (event) {
    const photoLink = event.target.closest('.product-reviews__card-image')
    if (!photoLink) return

    const card = photoLink.closest('[data-review-id]')
    const review = card ? this.findReviewById(card.dataset.reviewId) : null
    const src = photoLink.dataset.imageSrc || photoLink.getAttribute('href')
    if (!src) return

    if (this.galleryEl || this.querySelector('tb-reviews-gallery')) {
      event.preventDefault()
      this.openGallery({
        view: this.isDesktop() ? 'detail' : 'lightbox',
        photoFull: src,
        review,
      })
    }
  }
}

if (!customElements.get('tb-product-reviews')) {
  customElements.define('tb-product-reviews', TbProductReviews)
}
